import { StringHelper } from './../../../base/shared/string-helper';
import { ParkingSessionService } from '../parking-session.service';
import { DataManager } from '@syncfusion/ej2-data';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  ZoneDTO,
  ParkerRegistrationType,
  ParkerRegistrationOffense,
  ClientDTO,
  ParkingSessionDTO,
  PenaltyState,
  Currency,
  ParkerRegistrationWithParkerInfoDTO,
  ControlPlateType,
  IParkingSessionDTO
} from 'src/app/base/models/models.swagger.generated';
import { ParkerRegistrationDataType } from 'src/app/base/models/parker-registration-data-type.enum';
import { ClientService } from 'src/app/base/services/client/client.service';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { AvpCustomAdaptor } from 'src/app/base/shared/syncfusion/avp-custom-adaptor';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { CommandModel, QueryCellInfoEventArgs, GridModule, ToolbarItems, CommandClickEventArgs } from '@syncfusion/ej2-angular-grids';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { Tooltip } from '@syncfusion/ej2-angular-popups';
import { SfCommandButtonType, SfRequestTypes } from 'src/app/base/models/syncfusion-data';
import { ParkerRegistrationEvaluatedDetailComponent } from '../parker-registration-detail/parker-registration-evaluated-detail/parker-registration-evaluated-detail.component';
import { NgIf, NgClass, DatePipe } from '@angular/common';
import { AdjustParkerRegistrationEvaluatedDialogComponent } from './adjust-parker-registration-evaluated-dialog/adjust-parker-registration-evaluated-dialog.component';
import { EnumFilterDataManager } from 'src/app/base/shared/syncfusion/enum-filter-data-manager';
import { EnumFilterService } from 'src/app/base/services/enum-filter.service';

@Component({
    selector: 'app-parker-registration-evaluated-table',
    templateUrl: './parker-registration-evaluated-table.component.html',
    styleUrls: ['./parker-registration-evaluated-table.component.scss'],
    standalone: true,
    imports: [GridModule, NgIf, NgClass, ParkerRegistrationEvaluatedDetailComponent, DatePipe, TranslateModule, AdjustParkerRegistrationEvaluatedDialogComponent]
})
export class ParkerRegistrationEvaluatedTableComponent extends GridBaseComponent implements OnInit {
  @ViewChild(AdjustParkerRegistrationEvaluatedDialogComponent) public adjustRegistrationDialog: AdjustParkerRegistrationEvaluatedDialogComponent

  @Input() public zones: ZoneDTO[] = [];

  public ParkerRegistrationType = ParkerRegistrationType;
  public ParkerRegistrationOffense = ParkerRegistrationOffense;
  public ParkerRegistrationDataType = ParkerRegistrationDataType;
  public penaltyStateCancelled = PenaltyState.Cancelled;
  public currencyFilter = GridConstantsFactory.createCheckBoxFilter();
  public controlPlateTypeFilter = GridConstantsFactory.createCheckBoxFilter();
  public offenseFilter = GridConstantsFactory.createCheckBoxFilter();
  public calculatedParkingFeeField = 'calculatedParkingFee';
  public totalParkingFeeField = 'totalParkingFee';
  public toolbarOptions?: ToolbarItems[] = ['ColumnChooser'];

  private offensesFilterValues: OffenseEnumFilterType[] = [];
  public offensesField = 'offenses';

  constructor(public translate: TranslateService,
              clientService: ClientService,
              public parkingService: ParkingSessionService,
              private fb: UntypedFormBuilder,
              private spinnerService: SpinnerService,
              private enumFilterService: EnumFilterService) {
    super(clientService, translate, parkingService);
    this.gridCommands = [{
      type: SfCommandButtonType.Edit,
      title: this.translate.instant('i18n.domain.parker-registration.tooltips.view-details'),
      buttonOption:
      {
        cssClass: 'icon-btn',
        iconCss: 'icon-view e-icons'
      }
    } as CommandModel,
    {
      type: SfCommandButtonType.None,
      title: this.translate.instant('i18n.domain.parker-registration.tooltips.adjust-session'),
      buttonOption:
      {
        cssClass: 'icon-btn',
        iconCss: 'icon-edit e-icons'
      }
    } as CommandModel,
    ];
  }

  public ngOnInit(): void {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.parkingService, this.spinnerService)
    });
    this.isActiveColumnName = 'state';
    this.editDialogTitle = 'i18n.domain.parker-registration.dialog.header';
    this.addDialogTitle = '';
    this.addIndexFindColumnName = 'name';
    this.showSaveButton = false;

    const offenseType = ParkerRegistrationOffense;
    this.offensesFilterValues = this.enumFilterService.createEnumFilterArray(offenseType, this.offensesField);
  }

  public override actionBegin(args: any): void {
    if (args.requestType === SfRequestTypes.FilterBeforeOpen && args.columnName === this.offensesField) {
      args.filterModel.options.dataSource = new EnumFilterDataManager<OffenseEnumFilterType>(this.offensesFilterValues);
    }
    if (args.requestType === SfRequestTypes.FilterChoiceRequest && args.filterModel.options.field === this.offensesField) {
      args.query.distincts = []; // will set custom trigger flag and avoid filtering the list for distinct values of the enum filter field
      args.filterModel.options.dataManager = null;
    }
  }

  public searchById(id: number, array: any[]): any[] {
    return array.find(e => e.id === id);
  }

  public getClientById(id: number): ClientDTO {
    return this.clientService.clients.find(c => c.id === id);
  }

  public transformToHours(minutes: number): string {
    if (minutes) {
      return StringHelper.transformToTimeRepresentation(minutes);
    }
    return '00:00';
  }

  public refresh(): void {
    this.grid.refresh();
  }

  public formatCurrency(type: Currency): string {
    return 'i18n.shared.currencies.' + Currency[type];
  }

  public formatOffenseTypes(data: ParkingSessionDTO): string {
    if (!data || !data.parkerRegistrations) {
      return '';
    }
    return data.offenses.map(o => this.translateService.instant('i18n.domain.parker-registration.ParkerRegistrationOffense.' + ParkerRegistrationOffense[o])).join(', ');
  }

  public getCountryCode(data: ParkerRegistrationWithParkerInfoDTO): string {
    return data.knownTag ? data.knownTag.countryCode : data.unknownParkerTagCountryCode;
  }

  public formatControlPlateType(data: ParkerRegistrationWithParkerInfoDTO): string {
    return 'i18n.shared.controlPlateType.' + ControlPlateType[data.knownTag.controlPlateType];
  }

  public addTooltip(args: QueryCellInfoEventArgs) {
    var tooltip: Tooltip;
    if (args.column.field === this.calculatedParkingFeeField && (args.data as ParkingSessionDTO)?.calculationRoadmap) {
      tooltip =  new Tooltip({
        content: (args.data as ParkingSessionDTO).calculationRoadmap ?? ''
      }, args.cell as HTMLElement);
    }
    if (args.column.field === this.totalParkingFeeField && (args.data as ParkingSessionDTO)?.detailedPriceInformation) {
      const detailedPriceInformation = (args.data as ParkingSessionDTO).detailedPriceInformation;
      const tooltipContent = `
        <div class="custom-tooltip">
            <div class="bold">${this.translateService.instant('i18n.domain.parker-registration.parking-price-tooltip.calculation')}</div>
            <div class="flex">
                <span>${this.translateService.instant('i18n.domain.parker-registration.parking-price-tooltip.parking-price')}</span>
                <span>${detailedPriceInformation.parkingPrice.toFixed(2)} CHF</span>
            </div>
            <div class="flex">
                <span class="tab-right">${this.translateService.instant('i18n.domain.parker-registration.parking-price-tooltip.vat-included')}</span>
                <span>${detailedPriceInformation.parkingPriceIncludedVAT.toFixed(2)} CHF</span>
            </div>
            <div class="flex">
                <span>${this.translateService.instant('i18n.domain.parker-registration.parking-price-tooltip.service-fee')}</span>
                <span>${(detailedPriceInformation.parkingTransactionFeesConstant + detailedPriceInformation.parkingTransactionFeesVariable).toFixed(2)} CHF</span>
            </div>
            <div class="flex">
                <span class="tab-right">${this.translateService.instant('i18n.domain.parker-registration.parking-price-tooltip.vat-included')}</span>
                <span>${detailedPriceInformation.vat.toFixed(2)} CHF</span>
            </div>
            <hr>
            <div class="flex bold">
                <span>${this.translateService.instant('i18n.domain.parker-registration.parking-price-tooltip.total-parking-price')}</span>
                <span>${detailedPriceInformation.totalPriceWithVAT.toFixed(2)} CHF</span>
            </div>
        </div>
      `;
      tooltip = new Tooltip({
          content: tooltipContent,
          cssClass: 'custom-tooltip'
      }, args.cell as HTMLElement);
    }
  }

  public commandClicked(args: CommandClickEventArgs): void {
    if (args.commandColumn.type === SfCommandButtonType.None) {
      this.adjustRegistrationDialog?.openDialog(args.rowData as IParkingSessionDTO);
    }
  }

  public onSessionAdjustFunctionExecuted(): void {
    this.grid.refresh();
  }

  protected initializeEditForm(data: any): void {
    this.gridEditForm = this.fb.group({
    });
  }

  protected getEditDialogTitleParams(rowData: any) {
    return {params: ''};
  }
}

export type OffenseEnumFilterType = {
  type: ParkerRegistrationOffense;
}
